import { type ChatMessage, type ConversationSummary, isProblemDetails, type StreamEvent } from '@oneapp/shared';
import { ApiError, apiUrl, appCheckHeader, isSignInRefused, newRequestId, signedInToken } from '../lib/api';
import { createSseParser } from './sse';
import type { Connection } from './state';

export interface ChatStreamOptions {
  conversationId: string;
  /** The newest revision received from the stream: the server replays everything after it. */
  after: () => number;
  onEvent: (event: StreamEvent) => void;
  onConnection: (connection: Connection) => void;
  /** The server refused the sign-in itself (signed out elsewhere, blocked): the app signs out. */
  onSignInRefused: () => void;
  /** Silence for this long means the connection died quietly (the server pings every 15 s). */
  idleMs?: number;
}

export interface ChatStream {
  close(): void;
  /** Skip the wait and reconnect now (back online, or the app came back to the front). */
  reconnectNow(): void;
}

const MAX_DELAY_MS = 30_000;

/**
 * Live updates for one conversation over Server-Sent Events, read with fetch so the request can
 * carry the sign-in token. Reconnects on its own with growing, jittered delays, and resumes with
 * Last-Event-ID so nothing is missed or shown twice. Stops when the conversation is deleted or the
 * person signs out.
 */
export function openChatStream(options: ChatStreamOptions): ChatStream {
  const idleMs = options.idleMs ?? 45_000;
  let stopped = false;
  let current: AbortController | null = null;
  let reading: ReadableStreamDefaultReader<Uint8Array> | null = null;
  let wake: (() => void) | null = null;
  let hurry = false;
  let open = false;
  // The server suggests the base delay with `retry:`; until then, a second.
  let retryMs = 1_000;

  const path = `/api/v1/conversations/${encodeURIComponent(options.conversationId)}/stream`;

  /** One connection, until it ends. Returns how it ended, or throws why it couldn't start. */
  async function connect(freshToken: boolean): Promise<{ closed?: 'deleted' | 'signed_out'; heard: boolean }> {
    const controller = new AbortController();
    current = controller;
    const token = await signedInToken(freshToken);
    const response = await fetch(apiUrl(path), {
      headers: {
        Accept: 'text/event-stream',
        'Last-Event-ID': String(options.after()),
        'X-Request-Id': newRequestId(),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(await appCheckHeader()),
      },
      cache: 'no-store',
      signal: controller.signal,
    });
    if (!response.ok || !response.body) {
      const body: unknown = await response.json().catch(() => null);
      const problem = isProblemDetails(body) ? body : undefined;
      throw new ApiError(problem?.detail ?? `HTTP ${response.status}`, response.status, problem);
    }

    open = true;
    options.onConnection('open');
    let closed: 'deleted' | 'signed_out' | undefined;
    let heard = false;
    const parser = createSseParser({
      onRetry: (ms) => {
        retryMs = Math.max(250, ms);
      },
      onEvent: ({ event, data }) => {
        let payload: Record<string, unknown>;
        try {
          payload = JSON.parse(data);
        } catch {
          return;
        }
        if (event === 'message') options.onEvent({ type: 'message', message: payload as unknown as ChatMessage });
        else if (event === 'progress' && typeof payload.text === 'string')
          options.onEvent({ type: 'progress', text: payload.text });
        else if (event === 'typing') options.onEvent({ type: 'typing', on: payload.on === true });
        else if (event === 'conversation')
          options.onEvent({ type: 'conversation', conversation: payload as unknown as ConversationSummary });
        else if (event === 'closed') closed = payload.reason === 'signed_out' ? 'signed_out' : 'deleted';
      },
    });

    const reader = response.body.getReader();
    reading = reader;
    const decoder = new TextDecoder();
    // Cancelling the reader ends a pending read at once, whatever the network is doing.
    const giveUp = () => {
      controller.abort();
      reader.cancel().catch(() => {});
    };
    let idle = setTimeout(giveUp, idleMs);
    try {
      while (!closed) {
        const { done, value } = await reader.read();
        if (done) break;
        heard = true;
        clearTimeout(idle);
        idle = setTimeout(giveUp, idleMs);
        parser.push(decoder.decode(value, { stream: true }));
      }
    } finally {
      open = false;
      reading = null;
      clearTimeout(idle);
      reader.cancel().catch(() => {});
    }
    return closed ? { closed, heard } : { heard };
  }

  function pause(attempt: number): Promise<void> {
    if (hurry) {
      hurry = false;
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
      const delay = Math.min(MAX_DELAY_MS, retryMs * 2 ** Math.max(0, attempt - 1)) * (0.75 + Math.random() * 0.5);
      // Offline, there's no point trying until the connection is back ("online" wakes us).
      const timer = offline ? undefined : setTimeout(done, delay);
      function done() {
        clearTimeout(timer);
        wake = null;
        resolve();
      }
      wake = done;
    });
  }

  const end = (connection: Connection = 'closed') => {
    stopped = true;
    options.onConnection(connection);
  };

  void (async () => {
    let attempt = 0;
    let freshToken = false;
    while (!stopped) {
      options.onConnection(attempt === 0 ? 'connecting' : 'reconnecting');
      try {
        const outcome = await connect(freshToken);
        freshToken = false;
        if (stopped) return;
        if (outcome.closed) {
          options.onEvent({ type: 'closed', reason: outcome.closed });
          return end();
        }
        // The server ended a working stream (a restart): come back quickly.
        attempt = outcome.heard ? 1 : attempt + 1;
      } catch (error) {
        if (stopped) return;
        if (error instanceof ApiError) {
          if (error.problem?.code === 'TOKEN_EXPIRED' && !freshToken) {
            freshToken = true;
            continue;
          }
          if (isSignInRefused(error)) {
            end();
            return options.onSignInRefused();
          }
          if (error.status === 404) {
            options.onEvent({ type: 'closed', reason: 'deleted' });
            return end();
          }
        }
        attempt += 1;
      }
      if (stopped) return;
      options.onConnection(typeof navigator !== 'undefined' && navigator.onLine === false ? 'offline' : 'reconnecting');
      await pause(attempt);
    }
  })();

  const reconnectNow = () => {
    if (stopped || open) return;
    if (wake) wake();
    else hurry = true;
  };
  const onOnline = () => reconnectNow();
  const onOffline = () => {
    // Don't wait for a dead connection to time out.
    current?.abort();
    reading?.cancel().catch(() => {});
    options.onConnection('offline');
  };
  const onVisible = () => {
    if (document.visibilityState === 'visible') reconnectNow();
  };
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  document.addEventListener('visibilitychange', onVisible);

  return {
    close() {
      stopped = true;
      current?.abort();
      reading?.cancel().catch(() => {});
      wake?.();
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      document.removeEventListener('visibilitychange', onVisible);
    },
    reconnectNow,
  };
}
